import { observer } from 'mobx-react-lite';
import moment from 'moment';
import type { JSX } from 'react';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import type { Recommendation, TTDCampaign } from '@feathr/blackbox';
import { Alert, DatePicker } from '@feathr/components';
import { useUser } from '@feathr/extender/state';
import { momentToDate, TimeFormat } from '@feathr/hooks';

import { clampDateEndToCap, getExtendCap } from './extendCap';
import RecommendationModal from './RecommendationModal';

interface IProps {
  campaign: TTDCampaign;
  errors: string[];
  suppressAlert?: boolean;
  recommendation: Recommendation;
}

function ExtendEndDate({
  campaign,
  errors,
  suppressAlert = false,
  recommendation,
}: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const user = useUser();
  const timezone = user.get('timezone');

  const prevValue = recommendation.get('current_campaign_values').date_end;
  const prevMoment = moment.utc(prevValue);
  const prevValueFormatted = prevMoment
    .clone()
    .tz(timezone)
    .format(TimeFormat.pickerShortDateTimeZone);

  const { maxAllowedMoment, canExtend, effectiveMoment } = getExtendCap(campaign, recommendation);
  const effectiveValue = effectiveMoment.toISOString();
  const effectiveValueFormatted = effectiveMoment
    .clone()
    .tz(timezone)
    .format(TimeFormat.pickerShortDateTimeZone);

  useEffect(() => {
    if (canExtend) {
      handleTargetValueChange(effectiveValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleTargetValueChange(newValue?: string): void {
    if (!newValue) {
      campaign.set({ date_end: newValue });
      return;
    }
    // Clamp defensively — react-datepicker's maxDate disables whole days only,
    // so a user picking 23:59 on the cap day would tip getDays past the floor.
    campaign.set({
      date_end: clampDateEndToCap(newValue, campaign, recommendation),
    });
  }

  const alert = t('Extend end date from {{before}} to {{after}}', {
    before: prevValueFormatted,
    after: effectiveValueFormatted,
  });

  const helpText = t('Original end date: {{-date}}', { date: prevValueFormatted });

  return (
    <>
      {canExtend && !suppressAlert && <RecommendationModal.Alert description={alert} />}
      {!canExtend && (
        <Alert
          description={t(
            "This campaign can't be extended further while maintaining the current budget.",
          )}
          title={t('Extension not available')}
          type={'warning'}
        />
      )}
      <DatePicker
        autoComplete={'off'}
        dateFormat={'MMM d, yyyy h:mm aa'}
        helpPlacement={'bottom'}
        helpText={helpText}
        isFullWidth={true}
        label={t('New end date')}
        maxDate={momentToDate((canExtend ? maxAllowedMoment : prevMoment).clone().tz(timezone))}
        minDate={momentToDate(effectiveMoment.clone().tz(timezone))}
        name={'date_end'}
        onDateStrChange={handleTargetValueChange}
        showTimeSelect={true}
        timeIntervals={5}
        timezone={timezone}
        validationError={errors}
        value={campaign.get('date_end')}
      />
    </>
  );
}

export default observer(ExtendEndDate);
